import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, KeyRound, ScrollText, GaugeCircle, Users } from 'lucide-react';

const ITEMS = [
  {
    icon: ShieldCheck,
    title: 'SOC 2 Type II · ISO 27001',
    body: 'Audited annually. Letters and reports available under NDA. HIPAA-eligible workloads supported.',
    badge: 'Certified',
  },
  {
    icon: KeyRound,
    title: 'SSO + SCIM',
    body: 'SAML 2.0 / OIDC with Okta, Azure AD, Google Workspace, OneLogin. SCIM for automated provisioning. MFA enforced for all human access.',
    badge: 'Standard',
  },
  {
    icon: ScrollText,
    title: 'Customer audit trail',
    body: 'Every operator action against your tenant is logged in a separate immutable trail you can subscribe to via SIEM. JIT, time-bound, and recorded.',
    badge: 'Available',
  },
  {
    icon: GaugeCircle,
    title: '99.9% SLA · 24/7 support',
    body: 'Enterprise tier includes 99.9% availability SLA with credits, named CSM, and 24/7 P1 response under one hour.',
    badge: 'Enterprise',
  },
  {
    icon: Users,
    title: 'Role-based access (RBAC)',
    body: 'Built-in roles for CISO, Security Engineer, Engineering Lead, Auditor. Custom scopes per account, framework, or asset class.',
    badge: 'Standard',
  },
];

export default function EnterpriseReadiness() {
  return (
    <div data-testid="enterprise-readiness" className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-sm font-bold text-apex-ink">Enterprise readiness</h3>
          <p className="text-[11px] text-apex-neutral mt-1 max-w-xl">Procurement-ready. The questionnaire answers your security team is already drafting.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {ITEMS.map((it, i) => {
          const Icon = it.icon;
          return (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              data-testid={`enterprise-item-${it.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              className="rounded-lg border border-slate-200 bg-slate-50/40 p-3"
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-7 h-7 rounded-md bg-apex-lens/10 text-apex-lens inline-flex items-center justify-center shrink-0">
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <h4 className="text-sm font-semibold text-apex-ink truncate">{it.title}</h4>
                </div>
                <span className="text-[9px] uppercase tracking-wider text-apex-lens bg-apex-lens/10 border border-apex-lens/20 rounded px-1.5 py-0.5 font-semibold whitespace-nowrap">{it.badge}</span>
              </div>
              <p className="text-[12px] text-apex-ink-2 leading-relaxed">{it.body}</p>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
